import { ReactNode, useEffect, useRef } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Paragraph } from '@digdir/designsystemet-react';
import { ArrowLeftIcon } from '@navikt/aksel-icons';
import { Routes } from '../../enums/Routes';
import { useGetTeamByTeamId } from '../../hooks/queries/useGetTeamByTeamId';
import { useGetPastSurveys } from '../../hooks/queries/useGetPastSurveys';
import { AnalyzeSurveyProgress } from '../AnalyzeSurveyProgress/AnalyzeSurveyProgress';
import classes from './AnalysisLayout.module.css';

type AnalysisLayoutProps = {
  /** The questions in the analysis, in order, or undefined while unknown */
  questions?: string[];
  /** The question being shown, or 'summary' */
  step: number | 'summary';
  children: ReactNode;
};

/**
 * Top bar, tinted backdrop and step indicator shared by the analysis
 * question pages and the summary.
 */
export const AnalysisLayout = ({ questions, step, children }: AnalysisLayoutProps) => {
  const { t, i18n } = useTranslation();
  const { teamId, surveyId } = useParams();
  const mainRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  const { data: team } = useGetTeamByTeamId(Number(teamId));
  const { data: surveys } = useGetPastSurveys(Number(teamId));
  const survey = surveys?.find((s) => s.id === Number(surveyId));

  const date = survey
    ? new Date(survey.createdAt).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'nb-NO', {
        day: 'numeric',
        month: 'long',
      })
    : null;
  const meta = [team?.name, t('surveys.page.modules.psychologicalSafety'), date].filter(Boolean).join(' · ');

  // Moving between steps starts at the top, with focus on the new content
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    mainRef.current?.scrollIntoView?.({ block: 'start' });
    mainRef.current?.focus({ preventScroll: true });
  }, [step]);

  return (
    <div className={classes.page}>
      <header className={classes.topBar}>
        <div className={classes.topBarContent}>
          <NavLink to={Routes.Dashboard} className={classes.backLink} aria-label={t('analysis.backAria')}>
            <ArrowLeftIcon aria-hidden="true" fontSize="1.25rem" />
            {t('analysis.back')}
          </NavLink>
          <Paragraph data-size="sm" className={classes.meta}>
            {meta}
          </Paragraph>
        </div>
      </header>

      <div className={classes.hero} ref={mainRef} tabIndex={-1}>
        <div aria-hidden="true" className={classes.backdrop} />
        <div className={classes.content}>
          {questions && questions.length > 0 && <AnalyzeSurveyProgress questions={questions} step={step} />}
          {children}
        </div>
      </div>
    </div>
  );
};
